import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  TextField,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import { CloudUpload, Add } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { captureException } from '../utils/captureException';

const Lanzamiento: React.FC = () => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [projectCodes, setProjectCodes] = useState<string[]>([]);
  // const [serialCode, setSerialCode] = useState<string>('');
  const [newProjectCode, setNewProjectCode] = useState<string>('');
  const navigate = useNavigate();

  const addProjectCode = () => {
    if (newProjectCode.trim() && !projectCodes.includes(newProjectCode.trim())) {
      setProjectCodes([...projectCodes, newProjectCode.trim()]);
      setNewProjectCode('');
      setResult(null);
      setUploadedFile(null);
    }
  };

  const removeProjectCode = (codeToRemove: string) => {
    setProjectCodes(projectCodes.filter(code => code !== codeToRemove));
    setResult(null);
    setUploadedFile(null);
  };

  const handlePdfUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      
      if (!file) return;

      setUploading(true);
      setError(null);
      setSuccess(null);
      setResult(null);

      try {
        if (!user) {
          throw new Error('Usuario no autenticado');
        }
        
        const idToken = await user.getIdToken();
        setUploadedFile(file);
        
        const formData = new FormData();
        formData.append('pdf', file);
        // Append each project code separately - multer will handle them as an array
        projectCodes.forEach(code => {
          formData.append('project_codes', code);
        });

        const serialCode = projectCodes.join(',');
        if (serialCode.trim()) {
          formData.append('serial_code', serialCode.trim());
        }

        const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/extract/lanzamiento`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          throw new Error(errorData.error || errorData.message || `Error: ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log("Response:", responseData);
        setResult(responseData);
        setSuccess(`PDF procesado exitosamente`);
      } catch (err) {
        captureException(err, {
          tags: { feature: 'lanzamiento' },
          extra: { projectCodes, fileName: file?.name },
        });
        setError(err instanceof Error ? err.message : 'Error uploading PDF');
        setUploadedFile(null);
        setResult(null);
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  return (
    <main aria-label="Lanzamiento page" style={{ outline: 'none' }} tabIndex={-1}>
      <Container component="section" maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h6" color="text.secondary">
            Herramienta para la extracción de información de documentos de Lanzamiento.
          </Typography>
        </Box>

        {/* Configuration Section */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Card sx={{ maxWidth: 1000, width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Configuración de Extracción
              </Typography>
              
              {/* Project Codes */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Códigos de Proyecto
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {projectCodes.map((code, index) => (
                    <Chip
                      key={index}
                      label={code}
                      onDelete={() => removeProjectCode(code)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Nuevo código de proyecto"
                    value={newProjectCode}
                    onChange={(e) => setNewProjectCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addProjectCode()}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={addProjectCode}
                    startIcon={<Add />}
                    disabled={!newProjectCode.trim()}
                    sx={{ minWidth: 200 }}
                  >
                    Agregar
                  </Button>
                </Box>
              </Box>

              {/* Serial Code */}
              {/* <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Código de Serie (Opcional)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Código de serie del equipo"
                  value={serialCode}
                  onChange={(e) => setSerialCode(e.target.value)}
                />
              </Box> */}

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  aria-label="Cargar PDF"
                  onClick={handlePdfUpload}
                  disabled={uploading || projectCodes.length === 0}
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                  sx={{ minWidth: 200 }}
                >
                  {uploading ? 'Procesando...' : 'Seleccionar PDF'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* File Info */}
        {uploadedFile && !uploading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Card sx={{ maxWidth: 1000, width: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Archivo Seleccionado
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  <strong>Nombre:</strong> {uploadedFile.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  <strong>Tamaño:</strong> {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  <strong>Tipo:</strong> {uploadedFile.type}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
                  {/* For each project code add a button to navigate to the project. show in a grid of 2 columns */}
                  <Grid container spacing={2} width="100%">
                    {projectCodes.map((code) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={code} width="100%">
                        <Button 
                          variant="outlined" 
                          color="primary" 
                          fullWidth
                          onClick={() => {
                          navigate(`/project/${code}`);
                        }}>
                          Acceder al proyecto {code}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Results Section */}
        {result && !uploading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Card sx={{ maxWidth: 1000, width: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Resultados del Procesamiento
                </Typography>
                
                {result.success && (
                  <Box sx={{ mb: 3, width: '100%' }}>
                    <Typography variant="subtitle1" color="success.main" gutterBottom>
                      ✅ PDF procesado exitosamente
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Archivo:</strong> {result.filename} ({result.size} bytes)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Procesado:</strong> {new Date(result.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                )}

                {result.extraction && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      Información Extraída del Documento
                    </Typography>
                    
                    {/* Details Sections */}
                    {Object.keys(result.extraction).filter(key => !key.startsWith('OPCIONALES') && key.startsWith('DETALLES') && key !== 'errors' && !key.includes("path")).map(key => (
                      result.extraction[key] && Object.keys(result.extraction[key]).length > 0 && (
                        <Box key={key} sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {key.replace(/_/g, ' ')}
                          </Typography>
                          <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                            {Object.entries(result.extraction[key]).map(([fieldKey, fieldValue]) => (
                              <React.Fragment key={fieldKey}>
                                <Box sx={{ mb: 1, mt: 1, display: 'flex', alignItems: 'flex-start' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '340px', mr: 2 }}>
                                    {fieldKey}:
                                  </Typography>
                                  <Box sx={{ flex: 1 }}>
                                    {typeof fieldValue === 'object' && fieldValue !== null && 'value' in fieldValue ? (
                                      <Box>
                                        {(fieldValue as any).value !== null && (fieldValue as any).value !== undefined && (
                                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            {(fieldValue as any).value}
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2">
                                        {fieldValue ? String(fieldValue) : 'No especificado'}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Divider />
                              </React.Fragment>
                            ))}
                          </Paper>
                        </Box>
                      )
                    ))}

                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                      Opcionales Identificados
                    </Typography>
                    
                    {/* Opcionales Sections */}
                    {Object.keys(result.extraction).filter(key => key.startsWith('OPCIONALES')).map(key => (
                      result.extraction[key] && Array.isArray(result.extraction[key]) && result.extraction[key].length > 0 && (
                        <Box key={key} sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {key.replace('OPCIONALES_', '').replace(/_/g, ' ')}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {result.extraction[key].map((item: any, index: number) => (
                              <Paper key={index} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {item.n_opcion}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {item.in_lista && (
                                      <Chip label="En Lista" size="small" color="success" variant="outlined" />
                                    )}
                                    {item.ai === true && (
                                      <Chip label="AI Detectado" size="small" color="info" variant="outlined" />
                                    )}
                                    {item.operario_verificado && (
                                      <Chip label="Verificado" size="small" color="warning" variant="outlined" />
                                    )}
                                  </Box>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Descripción:</strong> {item.descripcion}
                                </Typography>
                                {item.descripcion_de_la_opcion && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Descripción Detallada:</strong> {item.descripcion_de_la_opcion}
                                  </Typography>
                                )}
                                {item.cantidad && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Cantidad:</strong> {item.cantidad}
                                  </Typography>
                                )}
                                {item.grupo && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Grupo:</strong> {item.grupo}
                                  </Typography>
                                )}
                                {item.posicion && Array.isArray(item.posicion) && item.posicion.length > 0 && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Posición:</strong> {item.posicion.join(', ')}
                                  </Typography>
                                )}
                                {item.tipo && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Tipo:</strong> {item.tipo}
                                  </Typography>
                                )}
                                {item.modelo_camion && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Modelo Camión:</strong> {item.modelo_camion}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        </Box>
                      )
                    ))}

                    {Object.keys(result.extraction).filter(key => key === 'errors').map(key => (
                      result.extraction[key] && Object.keys(result.extraction[key]).length > 0 && (
                        <Box key={key} sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Errores en la extracción
                          </Typography>
                          <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                            {Object.entries(result.extraction[key]).map(([fieldKey, fieldValue]) => (
                              <React.Fragment key={fieldKey}>
                                <Box sx={{ mb: 1, mt: 1, display: 'flex', alignItems: 'flex-start' }}>
                                  <Box sx={{ flex: 1 }}>
                                    {typeof fieldValue === 'object' && fieldValue !== null && 'value' in fieldValue ? (
                                      <Box>
                                        {(fieldValue as any).value !== null && (fieldValue as any).value !== undefined && (
                                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            {(fieldValue as any).value}
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2">
                                        {fieldValue ? String(fieldValue) : 'No especificado'}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Divider />
                              </React.Fragment>
                            ))}
                          </Paper>
                        </Box>
                      )
                    ))}

                    {/* Raw JSON for debugging */}
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Datos Completos (JSON)
                      </Typography>
                      <Box component="pre" sx={{ 
                        backgroundColor: 'grey.100', 
                        p: 2, 
                        borderRadius: 1, 
                        overflow: 'auto',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        maxHeight: '400px'
                      }}>
                        {JSON.stringify(result.extraction, null, 2)}
                      </Box>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Container>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default Lanzamiento;

